import { gql } from '@apollo/client';
import { IProgramRuleEntityAttributes, ProgramRuleEntity } from 'Models/Entities';
import { store } from 'Models/Store';

export const getProgramRulesRecursiveQuery = (levelLimit: number): string => {
	const childQueries = levelLimit > 0
		? getProgramRulesRecursiveQuery(levelLimit - 1)
		: '';

	return `
		programRules {
			id
			created
			modified
			name
			displayName
			description
			displayOnSidebar
			condition
			compulsory
			category
			parentProgramRuleId
			programVersionId
			rootProgramVersionId
			unitConditions {
				id
				ruleType
				units
				programRuleId
			}
			courses {
				id
				course {
					id
					code
					name
					level
					requisiteLogicGroups {
						id
						isTopLevelGroup
						description
					}
				}
			}
			${childQueries}
		}
	`;
};

export const DELETE_PROGRAM_RULES = gql`
	mutation deleteProgramRules($programRuleIds: [ID]){
		deleteProgramRuleEntity(programRuleEntityIds: $programRuleIds) {
			id
		}
	}
`;

export const DELETE_PROGRAM_RULE_COURSE = gql`
	mutation deleteProgramRuleCourse($programRuleCourseIds: [ID]){
		deleteProgramRuleCourse(programRuleCourseIds: $programRuleCourseIds) {
			id
		}
	}
`;

export const getProgramRulesByRootProgramVersionIdQuery = () => {
	return gql`
		query getProgramRulesByRootProgramVersionId($programVersionId: [String])  {
			programRuleEntitys(where: [{
				path: "programVersionId", comparison: equal, value: $programVersionId
			}]) {
				id
				name
				displayName
				description
				order
				programVersionId
				rootProgramVersionId
				parentProgramRuleId
				category
				created
				unitConditions {
					id
					ruleType
					units
					programRuleId
				}
				courses {
					id
					courseId
					programRuleId
					course {
						id
						code
						name
						level
						units
						requisiteLogicGroups {
							id
							isTopLevelGroup
							description
						}
					}
				}
			}
		}
	`;
};

/**
 * Get program rules for a given program version
 * @param abortSignal request controller abort signal
 * @param programVersionId program version id
 * @returns
 */
export const getProgramRules = async (
	abortSignal: AbortSignal,
	programVersionId?: string,
): Promise<ProgramRuleEntity[]> => {
	if (!programVersionId) {
		return [] as ProgramRuleEntity[];
	}

	const { data } = await store.apolloClient.query<{
		programRuleEntitys: IProgramRuleEntityAttributes[];
	}>({
		query: getProgramRulesByRootProgramVersionIdQuery(),
		variables: {
			programVersionId: programVersionId,
		},
		fetchPolicy: 'network-only',
		context: {
			fetchOptions: {
				signal: abortSignal,
			},
		},
	});

	return data.programRuleEntitys
		.map(programRule => new ProgramRuleEntity(programRule));
};
